// The shared report panel for Check Doc and Fix Format.
//
// Read-only and monospace: the output is aligned row numbers, unlike FormatFixPanel's
// deliberate font-sans prose. Each section is coloured by its issue type so the
// different kinds of problem are told apart at a glance.

/**
 * Tone -> Tailwind classes, written out in full.
 *
 * These must be complete literal strings. Tailwind scans the source for class names, so
 * anything built by interpolation (`border-${tone}-500`) is purged from the stylesheet
 * and silently renders unstyled.
 *
 * No green: it reads as "nothing wrong", and every section here is a problem.
 */
const TONES = {
  red: "border-red-500 bg-red-50 text-red-800",
  blue: "border-blue-500 bg-blue-50 text-blue-800",
  amber: "border-amber-500 bg-amber-50 text-amber-800",
  violet: "border-violet-500 bg-violet-50 text-violet-800",
  cyan: "border-cyan-600 bg-cyan-50 text-cyan-800",
  orange: "border-orange-500 bg-orange-50 text-orange-800",
  slate: "border-slate-400 bg-slate-50 text-slate-800",
  indigo: "border-indigo-500 bg-indigo-50 text-indigo-800",
  fuchsia: "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800",
  rose: "border-rose-500 bg-rose-50 text-rose-800",
  yellow: "border-yellow-500 bg-yellow-50 text-yellow-800",
};

const CourseReportPanel = ({ report, title, onReset, onCopy }) => {
  if (!report) return null;

  const sections = report.sections || [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
          {report.fileName && <p className="text-xs text-gray-500 mt-1">{report.fileName}</p>}
        </div>
        <div className="flex gap-2 shrink-0">
          {onCopy && (
            <button type="button" onClick={() => onCopy(report.text)} className="btn btn-secondary">
              Copy
            </button>
          )}
          <button type="button" onClick={onReset} className="btn btn-secondary">
            Reset
          </button>
        </div>
      </div>

      <p className="text-sm font-medium text-gray-700 mb-4">{report.summary}</p>

      {sections.length === 0 && (
        <p className="text-sm text-gray-500">Nothing to report.</p>
      )}

      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.key}
            className={`border-l-4 rounded-r px-4 py-3 ${TONES[section.tone] || TONES.slate}`}
          >
            <h3 className="font-semibold text-xs tracking-wide mb-2">
              {section.heading} ({section.count})
            </h3>
            <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-gray-800 max-h-72 overflow-y-auto">
              {section.lines.join("\n")}
            </pre>
          </div>
        ))}
      </div>

      {report.footer && (
        <p className="mt-4 font-mono text-xs text-gray-600 whitespace-pre-wrap">{report.footer}</p>
      )}
    </div>
  );
};

export default CourseReportPanel;
